import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../auth/AuthProvider'
import { config } from '../config'
import { useIntranetUnseen, useNotifications } from '../api/queries'
import { useRealtime } from '../realtime/useRealtime'
import { useOnlineStatus } from '../pwa/useOnlineStatus'
import { InstallBanner } from '../pwa/InstallBanner'
import { UpdateBanner } from '../pwa/UpdateBanner'
import { Avatar } from './ui'
import { Footer } from './Footer'
import { FieldMessageBanner } from './FieldMessageBanner'
import { GlobalSearch } from './GlobalSearch'
import { CreateMenu } from './CreateMenu'
import { PendingReceiptsBanner, usePendingReceiptCount } from './PendingReceiptsBanner'
import { InvoiceReadyBanner, useInvoiceReadyCountForOffice } from './InvoiceReadyBanner'
// Menupunkterne er faelles med "Mere"-siden paa mobil - se src/navigation.ts
import { pageTitles, useVisibleAdminNav, useVisibleBottomNav, useVisibleMainNav } from '../navigation'

/**
 * Rammen om hele brugerfladen: sidemenu på desktop, bundmenu på mobil
 * (kravspec afsnit 48), realtidsforbindelse og offline-besked.
 */
export function AppShell() {
  const { user } = useAuth()
  const location = useLocation()
  const queryClient = useQueryClient()
  const isOnline = useOnlineStatus()
  const { data: notifications } = useNotifications()
  const { data: intranetUnseen } = useIntranetUnseen()
  // Kvitteringer til godkendelse - kun kontoret (Simon 2026-10-01).
  const pendingReceipts = usePendingReceiptCount()
  const invoiceReady = useInvoiceReadyCountForOffice()

  const unread = notifications?.filter((n) => !n.isRead).length ?? 0

  // Rollerne og de slukkede moduler er allerede taget med i betragtning her -
  // se src/navigation.ts. Samme to lister bruges paa "Mere"-siden.
  const visibleMainNav = useVisibleMainNav()
  const visibleAdminNav = useVisibleAdminNav()
  const visibleBottomNav = useVisibleBottomNav()

  // Serveren fortæller kun at noget er ændret - vi henter selv friske data.
  const connection = useRealtime({
    onDataChanged: (event) => {
      // Vagtplanen (beslutning 120): et bytte besvares af en kollega - planen skal rette sig med det samme.
      if (event.entityType === 'intranet') {
        void queryClient.invalidateQueries({ queryKey: ['intranet', 'oncall'] })
      }
      // Varer til bilen (beslutning 127): lageret pakker, montoeren venter paa det.
      // MUS (beslutning 129): den anden har trykket "Klar" eller godkendt.
      if (event.entityType === 'apprentice') {
        void queryClient.invalidateQueries({ queryKey: ['intranet', 'apprentices'] })
      }
      if (event.entityType === 'mus') {
        void queryClient.invalidateQueries({ queryKey: ['intranet', 'mus'] })
      }
      // En ny kvittering skal straks give tal og stribe hos kontoret.
      if (event.entityType === 'receipt') {
        void queryClient.invalidateQueries({ queryKey: ['receipts'] })
      }
      if (event.entityType === 'van_order') {
        void queryClient.invalidateQueries({ queryKey: ['van-orders'] })
      }
      // Et akut udkald maa ikke vente paa naeste sideskift.
      void queryClient.invalidateQueries({ queryKey: ['field-messages'] })
      void queryClient.invalidateQueries({ queryKey: ['projects'] })
      void queryClient.invalidateQueries({ queryKey: ['tasks'] })
      void queryClient.invalidateQueries({ queryKey: ['me'] })
      void queryClient.invalidateQueries({ queryKey: ['activity'] })
      void queryClient.invalidateQueries({ queryKey: ['schedule'] })
    },
    onScreenStatus: () => {
      void queryClient.invalidateQueries({ queryKey: ['screens'] })
    }
  })

  const title = pageTitles[location.pathname] ?? 'Projekt Overblik'

  return (
    <div className="app">
      <nav className="sidebar">
        <div className="sidebar__brand">
          <img src="/logo/tc-mark.svg" alt="" width={34} height={28} />
          {config.appName}
        </div>

        {visibleMainNav.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/'}
            className={({ isActive }) => `nav-link ${isActive ? 'nav-link--active' : ''}`}
          >
            <span>{item.icon}</span>
            {item.label}
            {item.to === '/intranet' && (intranetUnseen?.total ?? 0) > 0 && (
              <span className="pill" style={{ marginLeft: 'auto', background: '#DC2626', color: '#fff' }}>
                {intranetUnseen!.total}
              </span>
            )}
            {item.to === '/receipts' && pendingReceipts > 0 && (
              <span className="pill" style={{ marginLeft: 'auto', background: '#DC2626', color: '#fff' }}>
                {pendingReceipts}
              </span>
            )}
            {item.to === '/invoicing' && invoiceReady > 0 && (
              <span className="pill" style={{ marginLeft: 'auto', background: '#DC2626', color: '#fff' }}>
                {invoiceReady}
              </span>
            )}
            {item.to === '/notifications' && unread > 0 && (
              <span className="pill" style={{ marginLeft: 'auto', background: '#DC2626', color: '#fff' }}>
                {unread}
              </span>
            )}
          </NavLink>
        ))}

        {visibleAdminNav.length > 0 && (
          <>
            <div className="sidebar__section">Administration</div>
            {visibleAdminNav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) => `nav-link ${isActive ? 'nav-link--active' : ''}`}
              >
                <span>{item.icon}</span>
                {item.label}
              </NavLink>
            ))}
          </>
        )}

        <div style={{ marginTop: 'auto' }}>
          <NavLink to="/more" className={({ isActive }) => `nav-link ${isActive ? 'nav-link--active' : ''}`}>
            <Avatar user={user} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{user?.displayName}</span>
          </NavLink>
        </div>
      </nav>

      <div className="main">
        <header className="topbar">
          <h1 className="topbar__title">{title}</h1>
          {/* Den store søgning (beslutning 110). */}
          <GlobalSearch />
          <span
            className="tiny muted"
            title={connection === 'connected' ? 'Realtidsforbindelse aktiv' : 'Ingen realtidsforbindelse'}
          >
            {connection === 'connected' ? '🟢 Live' : connection === 'connecting' ? '🟡 Forbinder' : '⚪ Offline'}
          </span>
          {/* "+" - alt, man kan oprette, ét sted (Simon, 2026-09-30). */}
          <CreateMenu />
        </header>

        {!isOnline && <div className="banner banner--offline">Du er offline. Viser senest hentede data.</div>}

        <main className="content">
          <InstallBanner />
          {/* Akutte udkald og beskeder fra kontoret staar oeverst paa hver side -
              de maa ikke kunne overses (beslutning 060). */}
          <FieldMessageBanner />
          {/* Kvitteringer til godkendelse - kun kontoret, paa alle sider (Simon 2026-10-01). */}
          <PendingReceiptsBanner />
          {/* Sager klar til faktura - kun kontoret, samme slags stribe (beslutning 147). */}
          <InvoiceReadyBanner />
          {/* "Ny version klar" (beslutning 099). Staar under de akutte beskeder -
              en ny version haster ikke lige saa meget som et udkald. */}
          <UpdateBanner />
          <Outlet />
        </main>

        <Footer />
      </div>

      <nav className="bottom-nav">
        {visibleBottomNav.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/'}
            className={({ isActive }) => `bottom-nav__item ${isActive ? 'bottom-nav__item--active' : ''}`}
          >
            <span className="bottom-nav__icon">
              {item.icon}
              {/* Tallet staar paa ikonet i bunden - der er ikke plads ved siden af
                  teksten paa en telefon (beslutning 103). */}
              {item.to === '/intranet' && (intranetUnseen?.total ?? 0) > 0 && (
                <span className="bottom-nav__badge">{intranetUnseen!.total}</span>
              )}
              {/* Kvitteringerne staar under "Mere" paa telefonen. */}
              {/* Til fakturering (beslutning 147) står også under "Mere" - tallene lægges sammen. */}
              {item.to === '/more' && pendingReceipts + invoiceReady > 0 && (
                <span className="bottom-nav__badge">{pendingReceipts + invoiceReady}</span>
              )}
            </span>
            {item.label}
          </NavLink>
        ))}
      </nav>
    </div>
  )
}
